import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { postEnquiry, errorMessage } from "@/lib/api";
import { Eyebrow, MaskLines, Reveal } from "./Motion";

const EMPTY = { name: "", email: "", company: "", subject: "", message: "" };
const MAP = "https://maps.google.com/maps?q=Dundrum%20Business%20Park%2C%20Dundrum%20Road%2C%20Dublin%2014%2C%20D14%20EC85&z=15&output=embed";

const Info = ({ icon: Icon, label, children }) => (
  <div className="flex gap-5 border-t border-white/10 py-6">
    <Icon className="mt-1 h-4 w-4 shrink-0 text-[#c8a97e]" />
    <div>
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#b4b2ad]">{label}</div>
      <div className="mt-2 text-lg text-[#f5f3ef]">{children}</div>
    </div>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postEnquiry({ ...form, company: form.company || null });
      setForm(EMPTY);
      toast.success("Message sent — a thank-you note is on its way to your inbox.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-[1600px] px-6 py-24 sm:px-10 sm:py-32 lg:px-14">
      <Eyebrow index="06">Contact</Eyebrow>
      <h2 className="mt-8 font-display text-6xl leading-[0.9] sm:text-7xl lg:text-8xl">
        <MaskLines inView lines={["Let's talk", <em key="b" className="text-[#c8a97e]">business.</em>]} />
      </h2>
      <div className="mt-20 grid grid-cols-1 gap-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Info icon={Mail} label="General enquiries"><a data-testid="contact-email" href="mailto:sales@julespound.ie" className="link-u">sales@julespound.ie</a></Info>
          <Info icon={Phone} label="Telephone"><a data-testid="contact-phone" href="tel:+35312079300" className="link-u">+353 1 207 9300</a></Info>
          <Info icon={MapPin} label="Showrooms">JAISUN House, Unit 1, Dundrum Business Park,<br />Dundrum Road, Dublin 14, D14 EC85</Info>
          <div className="relative mt-4 aspect-[4/3] overflow-hidden border border-white/10">
            <iframe data-testid="contact-map" title="Jaisun House map" src={MAP} loading="lazy" className="h-full w-full grayscale invert-[0.92] contrast-[0.9]" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
          <form data-testid="enquiry-form" onSubmit={submit} className="space-y-8">
            <p className="font-display text-3xl italic text-[#d6d3cd]">Send us a message</p>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <input required minLength={2} data-testid="enq-name" className="field" placeholder="Your name *" value={form.name} onChange={set("name")} />
              <input required type="email" data-testid="enq-email" className="field" placeholder="Email *" value={form.email} onChange={set("email")} />
              <input data-testid="enq-company" className="field" placeholder="Company" value={form.company} onChange={set("company")} />
              <input required minLength={2} data-testid="enq-subject" className="field" placeholder="Subject *" value={form.subject} onChange={set("subject")} />
            </div>
            <textarea required minLength={5} data-testid="enq-message" rows={5} className="field resize-none" placeholder="Your message *" value={form.message} onChange={set("message")} />
            <button type="submit" disabled={busy} data-testid="enq-submit" className="group flex items-center gap-3 rounded-full border border-[#f5f3ef] px-8 py-4 text-[13px] font-medium uppercase tracking-[0.16em] text-[#f5f3ef] transition-colors duration-300 hover:bg-[#f5f3ef] hover:text-[#0a0b0d] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />}
              Send message
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
